'use client'

import { useState, useEffect } from 'react'
import { motion, useScroll, useTransform, AnimatePresence } from 'motion/react'
import Link from 'next/link'

const Header = () => {
	const [isScrolled, setIsScrolled] = useState(false)
	const [isVisible, setIsVisible] = useState(true)
	const [lastScrollY, setLastScrollY] = useState(0)
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
	const { scrollY } = useScroll()

	const logoScale = useTransform(scrollY, [0, 50], [1, 0.9])

	useEffect(() => {
		const handleScroll = () => {
			const currentScrollY = window.scrollY
			setIsScrolled(currentScrollY > 10)
			setIsVisible(currentScrollY <= lastScrollY || currentScrollY < 100)
			setLastScrollY(currentScrollY)
		}

		window.addEventListener('scroll', handleScroll, { passive: true })
		return () => window.removeEventListener('scroll', handleScroll)
	}, [lastScrollY])

	const navItems = [
		{ label: 'Home', href: '#' },
		{ label: 'About', href: '#about' },
		{ label: 'Work', href: '#work' },
		{ label: 'Experience', href: '#experience' },
		{ label: 'Contact', href: '#contact' },
		{ label: 'Blog', href: 'https://blog.da4ndo.com' }
	]

	return (
		<>
			<motion.header
				className={`fixed top-0 left-0 right-0 z-50 px-6 py-4 transition-all duration-500 ${
					isScrolled
						? 'bg-white/70 dark:bg-black/70 backdrop-blur-xl'
						: 'bg-transparent'
				}`}
				initial={{ y: -100 }}
				animate={{ y: isVisible ? 0 : -100 }}
				transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
			>
				<div className="max-w-6xl mx-auto flex items-center justify-between">
					<motion.div className="text-xl font-semibold" style={{ scale: logoScale }}>
						<Link href="/"><span className="animate-gradient-text">Da4ndo</span></Link>
					</motion.div>

					{/* Desktop nav */}
					<nav className="hidden md:block">
						<ul className="flex space-x-8">
							{navItems.map((item) => (
								<motion.li key={item.label} whileHover={{ y: -2 }} whileTap={{ y: 0 }}>
									<Link
										href={item.href}
										target={item.href.startsWith('http') ? '_blank' : undefined}
										rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
										className="text-gray-700 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400 transition-colors duration-300"
									>
										{item.label}
									</Link>
								</motion.li>
							))}
						</ul>
					</nav>

					{/* Mobile menu button */}
					<button
						className="md:hidden p-2"
						onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
						aria-label="Toggle menu"
					>
						<div className="w-6 h-5 relative flex flex-col justify-between">
							<span className={`w-full h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
							<span className={`w-full h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`} />
							<span className={`w-full h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
						</div>
					</button>
				</div>
			</motion.header>

			{/* Mobile menu */}
			<AnimatePresence>
				{isMobileMenuOpen && (
					<>
						<motion.div
							className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							onClick={() => setIsMobileMenuOpen(false)}
						/>
						<motion.nav
							className="fixed right-0 top-0 bottom-0 z-50 w-64 bg-white dark:bg-[#0a0a0a] p-6 shadow-xl md:hidden"
							initial={{ x: '100%' }}
							animate={{ x: 0 }}
							exit={{ x: '100%' }}
							transition={{ type: 'spring', damping: 20 }}
						>
							<ul className="space-y-4 mt-12">
								{navItems.map((item) => (
									<motion.li key={item.label} whileTap={{ x: 5 }}>
										<Link
											href={item.href}
											target={item.href.startsWith('http') ? '_blank' : undefined}
											rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
											className="block py-2 text-gray-700 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400 transition-colors"
											onClick={() => setIsMobileMenuOpen(false)}
										>
											{item.label}
										</Link>
									</motion.li>
								))}
							</ul>
						</motion.nav>
					</>
				)}
			</AnimatePresence>
		</>
	)
}

export default Header
